import React from 'react';
import { useActiveTenantEditable } from './useActiveTenantEditable';
import { useGlobalsEditable } from './useGlobalsEditable';

/**
 * Phase 3/7 — read-only enforcement when viewing a remote (non-editable) env.
 *
 * History:
 *  1. `inert` on the whole subtree — froze legitimate browsing (tabs, links,
 *     pagination); a remote env looked broken.
 *  2. A pure marker — left every form FIELD editable; you could type and the
 *     page's dirty/Save state lit up even though the write could never persist.
 *  3. Containment-scoped event blocking (`root.contains(target)`) — fixed inline
 *     pages but MISSED MUI dialogs / wizards / menus, which render through React
 *     portals into `document.body`, OUTSIDE the routed-content div. That left
 *     every Create flow ("create action / adventure / CMS") fully editable.
 *
 * This version blocks the EDIT primitives document-wide (so portaled modals are
 * covered too) and EXCLUDES only explicitly-marked interactive chrome
 * (`data-readonly-interactive="true"` — the env switcher and global search) so
 * the user can still navigate and switch back to the editable env:
 *   - text entry: `beforeinput` / `paste` / `cut` / `drop` / `dragstart`,
 *   - toggles / select-opens: click / mousedown / Space|Enter keydown on
 *     checkbox / radio / switch / select / combobox,
 *   - form submission: the `submit` event.
 * It does NOT disable elements, so navigation (tabs, links, row-clicks, the env
 * switcher) and read affordances (open a view dialog, copy text, scroll) keep
 * working — but no edit lands, so every page's own dirty-gated Save/Create stays
 * disabled with NO per-page change, and forms can't be filled or submitted.
 *
 * The hard integrity guarantees remain the server write-fence (any mutating verb
 * on a non-editable env → 403) and the axios non-GET reject; this is the UX
 * layer that makes "read-only" actually feel — and look — read-only.
 */
const EDIT_EVENTS = ['beforeinput', 'paste', 'cut', 'drop', 'dragstart'] as const;
// Controls that mutate via click/keydown rather than `beforeinput`.
//
// #11 FIX — we do NOT include generic [role=combobox], .MuiSelect-select, or
// <select> here any more. Those are also used for filter / sort / pagination
// dropdowns which are READS, not writes — blocking them broke table sorting,
// rows-per-page, and other read affordances on remote-env browsing.
//
// Write-driving Selects must be marked with data-write-action="true" (or be
// wrapped in a WriteButton / WriteIconButton) — the WRITE_ACTION_SELECTOR
// backstop below catches them. That is the correct, explicit opt-in path
// (#19). Unmarked Selects = allowed reads. Marked Selects = blocked writes.
const TOGGLE_SELECTOR =
  'input[type=checkbox],input[type=radio],.MuiSwitch-root,.MuiCheckbox-root,.MuiRadio-root';
/** Mark a container with this so its controls stay live in a read-only env. */
export const READONLY_INTERACTIVE_ATTR = 'data-readonly-interactive';
const CHROME_SELECTOR = `[${READONLY_INTERACTIVE_ATTR}="true"]`;
/**
 * Hard backstop marker for WRITE affordances (clean-sweep). WriteButton /
 * WriteIconButton stamp this, and any hand-marked onClick mutation (menu items,
 * row actions, cards) can carry it too. ReadOnlyGuard blocks click + Enter/Space
 * on these document-wide on a read-only env — so even a write affordance that
 * somehow renders enabled (a missed wrap, a future page) cannot fire a mutation.
 * This is what makes the read-only guarantee hold by DEFAULT, not per-button.
 */
export const WRITE_ACTION_ATTR = 'data-write-action';
const WRITE_ACTION_SELECTOR = `[${WRITE_ACTION_ATTR}="true"]`;

/**
 * Which editability source the guard consults:
 *   - 'tenant'  (DEFAULT, unchanged) — useActiveTenantEditable (env-level AND
 *     per-tenant home). Used by the tenant Layout. Byte-identical to today.
 *   - 'globals' (WAVE H) — useGlobalsEditable (globals editable on every
 *     non-prod env when the flag is on; env-level dev01-only when off). Used by
 *     the Admin / global-catalog layout, which is NOT tenant-scoped and must not
 *     consult the per-tenant home gate.
 */
export type ReadOnlyScope = 'tenant' | 'globals';

const ReadOnlyGuard: React.FC<{
  children: React.ReactNode;
  scope?: ReadOnlyScope;
}> = ({ children, scope = 'tenant' }) => {
  // Both hooks are called unconditionally (rules-of-hooks) and we pick by scope.
  // WAVE G — tenant editability (env-level AND per-tenant home). With no tenant /
  // backend off this equals the env-level bit, so enforcement is byte-identical.
  const { editable: tenantEditable } = useActiveTenantEditable();
  // WAVE H — globals editability (non-prod when flag on; dev01-only when off).
  const globalsEditable = useGlobalsEditable();
  const editable = scope === 'globals' ? globalsEditable : tenantEditable;

  React.useEffect(() => {
    if (editable) return; // editable env → no enforcement

    // True when the event targets explicitly-allowed interactive chrome (the
    // env switcher / global search), so the user can navigate + escape.
    const inChrome = (t: EventTarget | null): boolean =>
      t instanceof Element && t.closest(CHROME_SELECTOR) !== null;

    // Block text-mutation primitives anywhere outside chrome. These events fire
    // only on editable fields / contenteditable / drag targets — never on plain
    // navigation — so a blanket block is safe.
    const blockEdit = (e: Event): void => {
      if (inChrome(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    // Block form submission outside chrome (covers Enter-to-submit and explicit
    // submit buttons in any create/edit form).
    const blockSubmit = (e: Event): void => {
      if (inChrome(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    // Block toggles / select-opens outside chrome.
    const blockToggle = (e: Event): void => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (inChrome(t)) return;
      // Space/Enter toggle a focused control; let other keys (Tab, arrows, copy)
      // through so keyboard navigation still works.
      if (e.type === 'keydown') {
        const key = (e as KeyboardEvent).key;
        if (key !== ' ' && key !== 'Enter' && key !== 'Spacebar') return;
      }
      // Block both: native toggles AND any explicitly-marked write affordance.
      // The write-action backstop catches onClick mutations (buttons, icon
      // buttons, menu items, cards) that aren't form primitives — the exact gap
      // that let "Create" / "Publish" fire on a read-only env.
      if (t.closest(TOGGLE_SELECTOR) || t.closest(WRITE_ACTION_SELECTOR)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    for (const evt of EDIT_EVENTS) document.addEventListener(evt, blockEdit, true);
    document.addEventListener('submit', blockSubmit, true);
    document.addEventListener('click', blockToggle, true);
    document.addEventListener('mousedown', blockToggle, true);
    document.addEventListener('keydown', blockToggle, true);
    return () => {
      for (const evt of EDIT_EVENTS) document.removeEventListener(evt, blockEdit, true);
      document.removeEventListener('submit', blockSubmit, true);
      document.removeEventListener('click', blockToggle, true);
      document.removeEventListener('mousedown', blockToggle, true);
      document.removeEventListener('keydown', blockToggle, true);
    };
  }, [editable]);

  if (editable) return <>{children}</>;
  return (
    <div data-read-only="true" data-testid="read-only-guard" style={{ display: 'contents' }}>
      {children}
    </div>
  );
};

export default ReadOnlyGuard;
